import { createRouter, createWebHistory } from "@ionic/vue-router";
import { RouteRecordRaw } from "vue-router";
import ReleaseView from "@/modules/release/views/ReleaseView.vue";
import { useCityStore } from "@/modules/city/store/city";
import { useLocaleStore } from "@/modules/locale/store/locale";
import i18n from "@/plugins/i18n";
import { trackEvent } from "@/plugins/analytics";

const routes: Array<RouteRecordRaw> = [
  {
    path: "/",
    component: ReleaseView,
  },

  {
    path: "/cities",
    name: "cities",
    component: () => import("@/modules/city/views/CitiesView.vue"),
  },
  {
    path: "/city/:city",
    name: "city",

    children: [
      {
        path: ":locale",
        name: "locale",
        children: [
          {
            path: "",
            name: "menu",
            component: () => import("@/layouts/MenuLayout.vue"),
            children: [
              {
                path: "information",
                name: "information",
                component: () => import("@/modules/place/views/PlaceView.vue"),
              },
              {
                path: "information/:category",
                name: "information-category",
                component: () =>
                  import("@/modules/place/views/PlaceCategoryView.vue"),
              },
              {
                path: "rights",
                name: "rights",
                component: () =>
                  import("@/modules/rights/views/RightsView.vue"),
              },
              {
                path: "rights/:category/",
                name: "rights-category",
                component: () =>
                  import("@/modules/rights/views/RightsCategoryView.vue"),
              },
              {
                path: "journal",
                name: "journal",
                component: () =>
                  import("@/modules/journal/views/JournalView.vue"),
              },
              {
                path: "journal/category/:category/",
                name: "journal-category",
                component: () =>
                  import("@/modules/journal/views/JournalCategoryView.vue"),
              },
              {
                path: "journal/article/:article/",
                name: "journal-article",
                component: () =>
                  import("@/modules/journal/views/ArticleView.vue"),
              },
              {
                path: "calendar",
                name: "calendar",
                component: () =>
                  import("@/modules/event/views/CalendarView.vue"),
              },
              {
                path: "question",
                name: "question",
                component: () =>
                  import("@/modules/question/views/QuestionView.vue"),
              },
              {
                path: "about",
                name: "about",
                component: () => import("@/modules/about/views/AboutView.vue"),
              },
              {
                path: "search",
                name: "search",
                component: () =>
                  import("@/modules/search/views/SearchView.vue"),
              },
              {
                path: "profile/:sub",
                name: "profile",
                component: () =>
                  import("@/modules/profile/views/ProfileView.vue"),
              },
            ],
          },
        ],
      },
    ],
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
});

router.beforeEach(async (to, from, next) => {
  const cityStore = useCityStore();
  useLocaleStore();

  if (
    to.params.city &&
    (!cityStore.city || cityStore.city.slug !== to.params.city)
  ) {
    cityStore.city =
      (await cityStore.getCitiesList()).find(
        (city) => city.slug === to.params.city
      ) || null;
    if (!cityStore.city) {
      next({ name: "cities" });
    }
  }

  if (
    to.params.locale &&
    !Array.isArray(to.params.locale) &&
    i18n.global.locale.value !== to.params.locale
  ) {
    i18n.global.locale.value = to.params
      .locale as typeof i18n.global.locale.value;
  }
  next();
});

router.afterEach((to) => {
  // Only non-personal props: route name plus content slugs. The profile
  // route's :sub (user id) is deliberately never sent.
  const props: Record<string, string> = {
    screen: (to.name as string) ?? to.path,
  };
  for (const key of ["city", "locale", "category", "article"] as const) {
    const value = to.params[key];
    if (typeof value === "string") props[key] = value;
  }
  trackEvent("screen_view", props);
});

export default router;
